import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Navigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";

import Navbar from "../../components/Navbar";
import zipzyAdminApiClient from "../../apiClients/zipzyAdminApiClient";

// India has no DST, so IST is always UTC+05:30
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const TIME_RANGE_OPTIONS = [
  { _id: "today", name: "Today" },
  { _id: "yesterday", name: "Yesterday" },
  { _id: "last_one_day", name: "Last 1 Day" },
  { _id: "this_week", name: "This Week" },
  { _id: "last_seven_days", name: "Last 7 Days" },
  { _id: "this_month", name: "This Month" },
  { _id: "last_thirty_days", name: "Last 30 Days" },
];

const SYSTEM_TOTAL_FIELDS = [
  { key: "numRides", label: "Rides" },
  { key: "revenue", label: "Revenue (₹)" },
  { key: "distanceTravelledInRides", label: "Distance in Rides (km)" },
  { key: "numAutoCancelledRides", label: "Auto Cancelled Rides" },
  { key: "numManuallyCancelledRides", label: "Manually Cancelled Rides" },
];

const DRIVER_FIELD_SECTIONS = [
  {
    title: "Shift",
    fields: [
      { label: "Avg. Shift Start", getValue: (driver) => driver.averageShiftStartTime ?? "—" },
      { label: "Avg. Shift End", getValue: (driver) => driver.averageShiftEndTime ?? "—" },
      { label: "Shift Days", getValue: (driver) => driver.numShiftDays },
    ],
  },
  {
    title: "Rides",
    fields: [
      { label: "Rides", getValue: (driver) => driver.numRides },
      { label: "Revenue (₹)", getValue: (driver) => driver.revenue },
      { label: "Distance in Rides (km)", getValue: (driver) => driver.distanceTravelledInRides },
      { label: "Auto Cancelled Rides", getValue: (driver) => driver.numAutoCancelledRides },
      { label: "Manually Cancelled Rides", getValue: (driver) => driver.numManuallyCancelledRides },
    ],
  },
  {
    title: "Availability (hours)",
    fields: [
      { label: "Idle", getValue: (driver) => driver.availability.IDLE },
      {
        label: "Booked, Waiting for Customer",
        getValue: (driver) => driver.availability.BOOKED_WAITING_FOR_CUSTOMER,
      },
      { label: "Heading to Pickup", getValue: (driver) => driver.availability.HEADING_TO_PICKUP },
      { label: "In Ride", getValue: (driver) => driver.availability.IN_RIDE },
      { label: "Heading Back", getValue: (driver) => driver.availability.HEADING_BACK },
      {
        label: "Heading Back, Booking Queued",
        getValue: (driver) => driver.availability.HEADING_BACK_BOOKING_QUEUED,
      },
      { label: "Offline (Manual)", getValue: (driver) => driver.availability.OFFLINE_MANUAL },
      { label: "Offline (Auto)", getValue: (driver) => driver.availability.OFFLINE_AUTO },
    ],
  },
];

// formats an epoch timestamp as an IST ISO string, e.g. 2026-10-15T00:00:00+05:30
function toIstIsoString(timestampMs) {
  return new Date(timestampMs + IST_OFFSET_MS).toISOString().replace(/\.\d{3}Z$/, "+05:30");
}

// IST day boundaries are computed explicitly, so the range does not depend on the device timezone
function getIstStartOfDayMs(timestampMs) {
  return Math.floor((timestampMs + IST_OFFSET_MS) / MS_PER_DAY) * MS_PER_DAY - IST_OFFSET_MS;
}

function getTimeRange(timeRangeOption) {
  const nowMs = Date.now();
  const startOfTodayMs = getIstStartOfDayMs(nowMs);
  const istNow = new Date(nowMs + IST_OFFSET_MS);

  let startMs;
  let endMs = nowMs;

  switch (timeRangeOption) {
    case "today":
      startMs = startOfTodayMs;
      break;

    case "yesterday":
      startMs = startOfTodayMs - MS_PER_DAY;
      endMs = startOfTodayMs;
      break;

    case "last_one_day":
      startMs = nowMs - MS_PER_DAY;
      break;

    case "this_week":
      // week starts on Sunday, as on the rides stats page
      startMs = startOfTodayMs - istNow.getUTCDay() * MS_PER_DAY;
      break;

    case "last_seven_days":
      startMs = nowMs - 7 * MS_PER_DAY;
      break;

    case "this_month":
      startMs = startOfTodayMs - (istNow.getUTCDate() - 1) * MS_PER_DAY;
      break;

    case "last_thirty_days":
    default:
      startMs = nowMs - 30 * MS_PER_DAY;
      break;
  }

  return {
    startDateTime: toIstIsoString(startMs),
    endDateTime: toIstIsoString(endMs),
  };
}

function formatIstDateTime(isoString) {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(isoString));
}

function getErrorMessage(error) {
  return (
    error?.userFriendlyMessage ??
    error?.userFriendlyError?.detail ??
    error?.detail ??
    error?.validationErrors?.join("\n") ??
    error?.code ??
    "Something went wrong."
  );
}

function SingleSelect({ label, options, value, onChange }) {
  return (
    <FormControl sx={{ width: "80%", mb: 1 }}>
      <InputLabel>{label}</InputLabel>

      <Select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        input={<OutlinedInput label={label} />}
      >
        {options.map((option) => (
          <MenuItem key={option._id} value={option._id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography variant="h6" sx={{ fontWeight: "bold", alignSelf: "flex-start", mt: 3, mb: 1 }}>
      {children}
    </Typography>
  );
}

function SystemTotalCards({ systemTotal }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
        gap: 1.5,
        width: "100%",
      }}
    >
      {SYSTEM_TOTAL_FIELDS.map((field) => (
        <Card key={field.key} variant="outlined">
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {field.label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {systemTotal[field.key]}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

function RidesByHourOfDayChart({ numRidesByHourOfDay }) {
  const chartData = Object.entries(numRidesByHourOfDay).map(([hourKey, numRides]) => ({
    hour: hourKey.split("_")[0],
    hourRange: hourKey.replace("_", ":00 – ") + ":00",
    numRides: numRides,
  }));

  return (
    <Box sx={{ width: "100%", height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={1} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <ChartTooltip
            labelFormatter={(_, payload) => payload?.[0]?.payload.hourRange ?? ""}
            formatter={(value) => [value, "Rides"]}
          />
          <Bar dataKey="numRides" fill="#1976d2" />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

const stickyFirstColumnSx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  backgroundColor: "background.paper",
  borderRight: 1,
  borderRightColor: "divider",
};

// fields in rows, drivers in columns; scrolls horizontally with the field column pinned and
// vertically with the driver names pinned. On phones the label column is kept narrow (wrapping
// labels and tighter cells), so that several drivers fit side by side.
function DriverWiseTable({ driverWise }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const cellSx = isPhone ? { px: 1, py: 0.75, fontSize: "0.75rem" } : {};
  const labelCellSx = {
    ...stickyFirstColumnSx,
    ...cellSx,
    whiteSpace: "normal",
    ...(isPhone && { width: 112, minWidth: 112, maxWidth: 112 }),
  };
  const valueCellSx = { ...cellSx, whiteSpace: "nowrap" };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ width: "100%", maxHeight: "70vh", overflow: "auto" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {/* pinned to both the top and the left edge, so it must sit above both */}
            <TableCell sx={{ ...labelCellSx, zIndex: 3, fontWeight: "bold" }}>Field</TableCell>
            {driverWise.map((driver) => (
              <TableCell
                key={driver.driverId}
                align="right"
                sx={{
                  ...cellSx,
                  fontWeight: "bold",
                  whiteSpace: "normal",
                  ...(isPhone && { minWidth: 64, maxWidth: 80 }),
                }}
              >
                {driver.driverName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {DRIVER_FIELD_SECTIONS.map((section) => [
            <TableRow key={section.title}>
              <TableCell sx={{ ...labelCellSx, fontWeight: "bold", backgroundColor: "grey.100" }}>
                {section.title}
              </TableCell>
              <TableCell
                colSpan={driverWise.length}
                sx={{ ...cellSx, backgroundColor: "grey.100" }}
              />
            </TableRow>,
            ...section.fields.map((field) => (
              <TableRow key={`${section.title}-${field.label}`} hover>
                <TableCell sx={labelCellSx}>{field.label}</TableCell>
                {driverWise.map((driver) => (
                  <TableCell key={driver.driverId} align="right" sx={valueCellSx}>
                    {field.getValue(driver)}
                  </TableCell>
                ))}
              </TableRow>
            )),
          ])}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MetricsReportPage() {
  const isLoggedIn = zipzyAdminApiClient.isLoggedIn();

  const [selectedTimeRangeOption, setSelectedTimeRangeOption] = useState("yesterday");
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [report, setReport] = useState(null);

  const handleRunQueryButtonClick = async () => {
    setIsLoadingReport(true);
    setReport(null);

    const response = await zipzyAdminApiClient.getPeriodicMetricsReport(
      getTimeRange(selectedTimeRangeOption)
    );

    setIsLoadingReport(false);

    if (!response.success) {
      window.alert(getErrorMessage(response.error));
      return;
    }

    setReport(response.data);
  };

  if (!isLoggedIn) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      minHeight="100vh"
      textAlign="center"
      sx={{ pb: 10 }}
    >
      <Navbar />

      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
        Metrics Report
      </Typography>

      <SingleSelect
        label="Select Time Range"
        options={TIME_RANGE_OPTIONS}
        value={selectedTimeRangeOption}
        onChange={setSelectedTimeRangeOption}
      />

      <Box sx={{ mb: 1, display: "flex", justifyContent: "flex-end", width: "80%" }}>
        <Button
          color="primary"
          variant="contained"
          disabled={isLoadingReport}
          onClick={handleRunQueryButtonClick}
        >
          Run Query
        </Button>
      </Box>

      {isLoadingReport && <CircularProgress size={24} sx={{ mt: 2 }} />}

      {report !== null && (
        <Box
          sx={{
            width: { xs: "94%", md: "90%" },
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            textAlign: "left",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {formatIstDateTime(report.query.effectiveStartDateTime)} –{" "}
            {formatIstDateTime(report.query.effectiveEndDateTime)} IST
            {report.query.ignoredLocalityIds.length > 0 &&
              ` · ${report.query.ignoredLocalityIds.length} locality(s) excluded`}
          </Typography>

          <SectionTitle>System Total</SectionTitle>
          <SystemTotalCards systemTotal={report.systemTotal} />

          <SectionTitle>Rides by Hour of Booking (IST)</SectionTitle>
          <RidesByHourOfDayChart numRidesByHourOfDay={report.systemTotal.numRidesByHourOfDay} />

          <SectionTitle>Driver Wise</SectionTitle>
          {report.driverWise.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No drivers found.
            </Typography>
          ) : (
            <DriverWiseTable driverWise={report.driverWise} />
          )}
        </Box>
      )}
    </Box>
  );
}
